import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { isPasswordRecoveryEmailNotFoundError } from '../../../util/errors';

import { Form, PrimaryButton, FieldTextInput, NamedLink } from '../../../components';

import css from './PasswordRecoveryForm.module.css';

function PasswordRecoveryFormComponent({
  rootClassName = null,
  className = null,
  formId = null,
  inProgress = false,
  recoveryError = null,
  ...props
}) {
  const submittedValues = useRef({});

  return (
    <FinalForm
      rootClassName={rootClassName}
      className={className}
      formId={formId}
      inProgress={inProgress}
      recoveryError={recoveryError}
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          pristine,
          initialValues,
          inProgress,
          recoveryError,
          values,
        } = fieldRenderProps;

        // email
        const emailLabel = 'Email';
        const emailPlaceholder = 'Enter your email address';
        const emailRequiredMessage = 'Email is required';
        const emailNotFoundMessage = 'No account found with that email.';
        const emailInvalidMessage = 'Invalid email address';

        const emailRequired = validators.required(emailRequiredMessage);
        const emailValid = validators.emailFormatValid(emailInvalidMessage);

        // In case a given email is not found, pass a custom error message
        // to be rendered with the input component
        const customErrorText = isPasswordRecoveryEmailNotFoundError(recoveryError)
          ? emailNotFoundMessage
          : null;
        const initialEmail = initialValues ? initialValues.email : null;
        const emailTouched = values.email !== submittedValues.current.email;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submittedOnce = Object.keys(submittedValues.current).length > 0;
        const pristineSinceLastSubmit = submittedOnce && isEqual(values, submittedValues.current);
        const submitDisabled =
          (pristine && !initialEmail) || submitInProgress || pristineSinceLastSubmit;

        const loginLink = (
          <NamedLink name="LoginPage" className={css.modalHelperLink}>
            Log in
          </NamedLink>
        );

        return (
          <Form
            className={classes}
            onSubmit={e => {
              submittedValues.current = values;
              handleSubmit(e);
            }}
          >
            <FieldTextInput
              className={css.email}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
              customErrorText={emailTouched ? null : customErrorText}
            />

            <div className={css.bottomWrapper}>
              <p className={css.bottomWrapperText}>
                <span className={css.modalHelperText}>To log in instead, {loginLink}</span>
              </p>

              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                Send password reset instructions
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
}



const { bool, string } = PropTypes;

PasswordRecoveryFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,

  inProgress: bool,
  recoveryError: propTypes.error,
};

export default PasswordRecoveryFormComponent;
